// School Calendar (Module B) — the shared term calendar, now browsable at four zoom levels
// (Week · Month · Term · Year) on top of the open-edit spine. The four VIEWS (src/calendar-view.ts)
// are read/navigate surfaces; the full add / edit / remove / undo / restore spine is preserved
// verbatim under a "Manage the calendar" disclosure. Team-tier; calm, factual; a removed entry is
// RECOVERABLE (soft-delete), never a red alarm.

import { api } from "../api";
import { esc } from "../dom";
import { me } from "../state";
import { t, type StringKey } from "../i18n";
import { pageHeader } from "../ui";
import {
  switcher,
  periodNav,
  monthGrid,
  weekView,
  termStrip,
  yearGlance,
  todayIso,
  weekOf,
  termWeekRange,
  adjacentWeekAnchor,
  fmtEntryRange,
  fmtWhen,
  type ViewKind,
  type Entry,
  type WeekLesson,
  type TermLike,
} from "../calendar-view";

interface CalChange {
  entry_id: string;
  action: "create" | "edit" | "delete" | "restore";
  actor_name: string;
  created_at: string;
}

type EntryType = Entry["type"];
interface WeekData {
  week_no: number;
  week: { start: string; end: string };
  entries: Entry[];
  lessons: WeekLesson[];
}

const TYPES: EntryType[] = ["teaching", "test", "event", "holiday", "training"];
const typeLabel = (ty: EntryType): string => t(`type.${ty}` as StringKey);
function typeOptions(selected: EntryType): string {
  return TYPES.map((ty) => `<option value="${ty}"${ty === selected ? " selected" : ""}>${typeLabel(ty)}</option>`).join("");
}

// P1b ruling 2/7 (2026-09-04): grade scope + a "teaching happens" tick. No grade box ticked = every
// grade (the "All" chip and an empty selection are the same state — simplest honest UI). Hidden for
// `teaching` (a coursebook day is never grade-scoped the way a test/event/holiday can be).
function gradeChipsHtml(selected: number[] | null, idPrefix: string): string {
  const sel = new Set(selected ?? []);
  const allChecked = sel.size === 0;
  const chip = (label: string, checked: boolean, value: string): string =>
    `<label class="chip-check${checked ? " on" : ""}"><input type="checkbox" name="grade" value="${value}" ${checked ? "checked" : ""} /> ${label}</label>`;
  const grades = Array.from({ length: 12 }, (_, i) => i + 1)
    .map((g) => chip(`G${g}`, sel.has(g), String(g)))
    .join("");
  return `<div class="grade-chips" id="${idPrefix}-grades">${chip("All", allChecked, "all")}${grades}</div>`;
}
function teachingDayHtml(checked: boolean, idPrefix: string): string {
  return `<label style="display:flex;align-items:center;gap:8px;font-size:var(--text-sm);color:var(--color-ink-soft)">
    <input type="checkbox" id="${idPrefix}-teaching-day" name="teaching_day" ${checked ? "checked" : ""} /> ${t("cal.teaching_day")}
  </label>`;
}
/** Grade-chip mutual exclusion: ticking "All" clears every grade box and vice versa; if every
 *  grade box ends up unticked, "All" re-ticks itself — there is always exactly one clear state. */
function wireGradeChips(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(".grade-chips").forEach((box) => {
    box.addEventListener("change", (e) => {
      const t2 = e.target as HTMLInputElement;
      if (t2.value === "all") {
        if (t2.checked) box.querySelectorAll<HTMLInputElement>('input:not([value="all"])').forEach((i) => (i.checked = false));
      } else if (t2.checked) {
        const all = box.querySelector<HTMLInputElement>('input[value="all"]')!;
        all.checked = false;
      }
      const anyGrade = [...box.querySelectorAll<HTMLInputElement>('input:not([value="all"])')].some((i) => i.checked);
      if (!anyGrade) box.querySelector<HTMLInputElement>('input[value="all"]')!.checked = true;
    });
  });
}
function readGrades(form: HTMLElement): number[] | null {
  const vals = [...form.querySelectorAll<HTMLInputElement>('.grade-chips input:not([value="all"]):checked')].map((i) => Number(i.value));
  return vals.length ? vals : null;
}

// ── date helpers for the browse ranges (UTC-noon anchored, like _lib/time.ts) ──
function isoOf(dt: Date): string {
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}
function monthRange(anchor: string): { from: string; to: string } {
  const [y, m] = anchor.split("-").map(Number) as [number, number, number];
  const first = new Date(Date.UTC(y, m - 1, 1, 12));
  const mondayOffset = (first.getUTCDay() + 6) % 7;
  const start = new Date(first.getTime() - mondayOffset * 86_400_000);
  return { from: isoOf(start), to: isoOf(new Date(start.getTime() + 41 * 86_400_000)) };
}
function ayWindow(anchor: string): { from: string; to: string; startYear: number } {
  const [y, m] = anchor.split("-").map(Number) as [number, number, number];
  const startYear = m >= 10 ? y : y - 1; // academic year runs Oct → Sep
  return { from: `${startYear}-10-01`, to: `${startYear + 1}-09-30`, startYear };
}
function shiftMonths(anchor: string, delta: number): string {
  const [y, m, d] = anchor.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1 + delta, 1, 12));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function shiftDays(anchor: string, delta: number): string {
  const [y, m, d] = anchor.split("-").map(Number) as [number, number, number];
  return isoOf(new Date(Date.UTC(y, m - 1, d, 12) + delta * 86_400_000));
}

export async function renderCalendar(app: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  // Page-local state (this closure; reset when the page is re-entered).
  let view: ViewKind = "month";
  let anchor = todayIso();
  let editingId: string | null = null;
  let undo: { id: string; label: string } | null = null;
  let err: string | null = null;

  let term: (TermLike & { id: string }) | null = null;
  let currentWeek = 1;
  let isCurrentTerm = false; // is the anchor's term the live (contains-today) term?
  let prevStart: string | null = null; // start_date of the previous/next term (for term navigation)
  let nextStart: string | null = null;
  let weekData: WeekData | null = null;
  let viewEntries: Entry[] = []; // month / year / term
  let manage: { term: Entry[]; deleted: Entry[] } = { term: [], deleted: [] };
  let latestChange = new Map<string, CalChange>(); // entry_id → its most recent change (0019)
  const isLead = me()?.auth_role === "lead"; // is_key is Lead-only to set/clear
  let quickAddDate: string | null = null; // set by clicking an empty month day (Job B §4)
  // render() fully rebuilds app.innerHTML on every call (including after every add/edit/remove —
  // mutate() always re-renders), so without tracking this the <details> would slam shut after the
  // FIRST save of any Manage action, not just once. Set from the native `toggle` event, so a
  // manual open/close by the teacher is what persists across the next re-render.
  let manageOpen = false;

  // A ?week=N deep-link opens the Week view on that week (from plan-view / this-week / term strip).
  const qWeek = Number(query.get("week"));
  const qView = query.get("view");
  if (qView === "week" || qView === "month" || qView === "term" || qView === "year") view = qView;

  function clampWeek(n: number): number {
    const max = term?.week_count ?? n;
    return Math.min(Math.max(n, 1), max);
  }
  function weekNoForAnchor(): number {
    return term ? clampWeek(weekOf(anchor, term)) : 1;
  }

  async function refresh(): Promise<void> {
    // Resolve the term for the current anchor (not just "now") — this is what lets Week/Term views
    // cross term boundaries across the whole academic year.
    const tc = await api<{
      term: TermLike & { id: string };
      current_week: number;
      is_current: boolean;
      prev_start: string | null;
      next_start: string | null;
    }>(`/api/terms/current?date=${anchor}`).catch(() => null);
    term = tc?.term ?? null;
    currentWeek = tc?.current_week ?? 1;
    isCurrentTerm = tc?.is_current ?? false;
    prevStart = tc?.prev_start ?? null;
    nextStart = tc?.next_start ?? null;

    // On first entry, honor a ?week=N deep-link by aligning the anchor to that term-week.
    if (qWeek >= 1 && term) {
      view = "week";
      anchor = weekStartIso(clampWeek(qWeek));
    }

    // The Manage list (editable entries of the LIVE term + recently removed) — always under the fold.
    const [termScope, deleted, changes] = await Promise.all([
      api<{ entries: Entry[] }>("/api/calendar?scope=term"),
      api<{ entries: Entry[] }>("/api/calendar?scope=deleted"),
      api<{ changes: CalChange[] }>("/api/calendar?scope=changes"),
    ]);
    manage = { term: termScope.entries, deleted: deleted.entries };
    // Newest first from the API — the FIRST hit per entry_id is that entry's latest change.
    latestChange = new Map();
    for (const c of changes.changes) if (!latestChange.has(c.entry_id)) latestChange.set(c.entry_id, c);

    // View-specific data.
    if (view === "week") {
      const tp = term ? `&term=${encodeURIComponent(term.id)}` : "";
      weekData = await api<WeekData>(`/api/calendar?scope=week&week=${weekNoForAnchor()}${tp}`);
    } else if (view === "month" || view === "year") {
      const { from, to } = view === "month" ? monthRange(anchor) : ayWindow(anchor);
      viewEntries = (await api<{ entries: Entry[] }>(`/api/calendar?scope=range&from=${from}&to=${to}`)).entries;
    } else if (view === "term" && term) {
      // The anchor term's own entries (so a past/future term shows its own strip, not the live one's).
      viewEntries = (
        await api<{ entries: Entry[] }>(
          `/api/calendar?scope=range&from=${term.start_date}&to=${term.end_date}`,
        )
      ).entries;
    }
  }

  /** True when no active entry (single-day or spanning) covers `day` — Month view uses this to
   *  decide click → quick-add vs. click → drill into Week (Job B §4). */
  function dayIsEmpty(day: string): boolean {
    return !viewEntries.some((e) => day >= e.entry_date && day <= (e.end_date ?? e.entry_date));
  }

  function weekStartIso(n: number): string {
    return term ? termWeekRange(term, n).start : anchor;
  }

  function periodLabel(): string {
    if (view === "week") return weekData ? t("common.week_n", { n: weekData.week_no }) : "";
    if (view === "month") {
      const [y, m] = anchor.split("-").map(Number) as [number, number];
      return `${t(`mo.${m}` as StringKey)} ${y}`;
    }
    if (view === "term") return term?.name ?? "";
    const { startYear } = ayWindow(anchor);
    return `AY ${startYear}/${String(startYear + 1).slice(2)}`;
  }

  function viewHtml(): string {
    const today = todayIso();
    if (view === "week") {
      if (!weekData) return `<p class="empty">${t("week.empty")}</p>`;
      const range = weekData.week ?? { start: anchor, end: anchor };
      return (
        weekView(range, weekData.entries, weekData.lessons, today) +
        `<a class="btn secondary" data-link href="/plan/start" style="margin-top:var(--space-4)">${t("cal.plan_this_week")}</a>`
      );
    }
    if (view === "month") return monthGrid(anchor, viewEntries, today);
    if (view === "term") {
      // Highlight the live current week only when viewing the term that actually contains today.
      const highlight = isCurrentTerm ? currentWeek : -1;
      return term ? termStrip(term, viewEntries, highlight) : `<p class="empty">${t("week.empty")}</p>`;
    }
    const { from, to } = ayWindow(anchor);
    return yearGlance(from, to, viewEntries, today);
  }

  // ── Manage the calendar (the preserved open-edit spine) ────────────────────────────────
  // "edited by {name} · {when}" under a row — whoever touched it last, create or edit alike, so
  // the department can see updates without opening the change log (Job B §4).
  const changeLine = (e: Entry): string => {
    const c = latestChange.get(e.id);
    return c ? `<div class="cal-changed muted">${t("cal.edited_by", { name: c.actor_name, when: fmtWhen(c.created_at) })}</div>` : "";
  };

  const staticRow = (e: Entry): string =>
    `<div class="cal-row"><span class="cal-date">${esc(fmtEntryRange(e))}</span>` +
    `<span class="cal-label">${esc(e.label)}</span><span class="chip ${e.type}${e.is_key ? " key" : ""}">${
      e.is_key ? "📌 " : ""
    }${typeLabel(e.type)}</span>` +
    `<button class="linkbtn" type="button" data-act="edit" data-id="${esc(e.id)}" aria-label="Edit ${esc(e.label)}">${t("common.edit")}</button></div>` +
    changeLine(e);

  const editFormRow = (e: Entry): string =>
    `<form class="cal-edit" data-id="${esc(e.id)}" data-type="${e.type}">
      <input type="date" name="entry_date" value="${esc(e.entry_date)}" required aria-label="Date" />
      <label class="hint" for="until-${esc(e.id)}" style="margin:0">${t("cal.until")}</label>
      <input id="until-${esc(e.id)}" type="date" name="end_date" value="${esc(e.end_date ?? "")}" aria-label="${t("cal.until")}" />
      <input name="label" value="${esc(e.label)}" required aria-label="Label" />
      <select name="type" aria-label="Type">${typeOptions(e.type)}</select>
      <div class="grade-field" ${e.type === "teaching" ? "hidden" : ""}>
        <label class="hint" style="margin:0">${t("cal.grades")}</label>
        ${gradeChipsHtml(e.grades, `edit-${e.id}`)}
      </div>
      ${teachingDayHtml(e.teaching_day, `edit-${e.id}`)}
      ${
        isLead
          ? `<label style="display:flex;align-items:center;gap:8px;font-size:var(--text-sm);color:var(--color-ink-soft)">
               <input type="checkbox" name="is_key" ${e.is_key ? "checked" : ""} /> ${t("cal.key_toggle")}
             </label>`
          : ""
      }
      <div class="row-actions">
        <button class="btn sm" type="submit">${t("common.save")}</button>
        <button class="btn sm secondary" type="button" data-act="cancel">${t("common.cancel")}</button>
        <button class="linkbtn quiet" type="button" data-act="remove" data-id="${esc(e.id)}">${t("common.remove")}</button>
      </div>
    </form>`;

  const editableRow = (e: Entry): string => (e.id === editingId ? editFormRow(e) : staticRow(e));

  const removedRow = (e: Entry): string =>
    `<div class="cal-row removed"><span class="cal-date">${esc(fmtEntryRange(e))}</span>` +
    `<span class="cal-label">${esc(e.label)}</span><span class="chip ${e.type}">${typeLabel(e.type)}</span>` +
    `<button class="linkbtn" type="button" data-act="restore" data-id="${esc(e.id)}">${t("cal.restore")}</button></div>`;

  function manageBody(): string {
    // Read only — render() runs on EVERY state change (switching views, editing a different row,
    // an unrelated refresh()), not just once per quick-add, so clearing this here would drop the
    // teacher's pending date the moment anything else re-rendered. It's cleared explicitly instead:
    // on a successful add-form submit, or when the panel is closed (the `toggle` listener below).
    const prefillDate = quickAddDate;
    const byWeek = new Map<number, Entry[]>();
    for (const e of manage.term) {
      const w = term ? weekOf(e.entry_date, term) : 0;
      (byWeek.get(w) ?? byWeek.set(w, []).get(w)!).push(e);
    }
    // h2: each week is a real heading level, not just a bold label (F-2026-09-03-10) — h3 stays
    // for the add-form / recently-removed sub-sections below.
    const weekLabel = (w: number): string => (w <= 0 ? t("cal.before_term") : t("common.week_n", { n: w }));
    const termHtml = [...byWeek.keys()]
      .sort((a, b) => a - b)
      .map((w) => `<h2>${weekLabel(w)}</h2>${byWeek.get(w)!.map(editableRow).join("")}`)
      .join("");

    const removed = manage.deleted;
    const removedHtml = removed.length
      ? `<h3>${t("cal.recently_removed")}</h3><p class="hint">${t("cal.removed_hint")}</p>${removed.map(removedRow).join("")}`
      : "";

    return `
      ${manage.term.length ? termHtml : `<p class="empty">${t("cal.nothing_upcoming")}</p>`}
      <h3>${t("cal.add_title")}</h3>
      <form id="addentry">
        <label for="cal-date">${t("cal.date")}</label>
        <input id="cal-date" name="entry_date" type="date" required value="${esc(prefillDate ?? "")}" />
        <label for="cal-until">${t("cal.until")}</label>
        <input id="cal-until" name="end_date" type="date" />
        <p class="hint" style="margin:2px 0 0">${t("cal.until_hint")}</p>
        <label for="cal-label">${t("cal.label")}</label>
        <input id="cal-label" name="label" placeholder="${t("cal.label_placeholder")}" required />
        <label for="cal-type">${t("cal.type")}</label>
        <select id="cal-type" name="type">${typeOptions("event")}</select>
        <div class="grade-field" id="add-grade-field">
          <label class="hint" style="margin:0">${t("cal.grades")}</label>
          ${gradeChipsHtml(null, "add")}
        </div>
        ${teachingDayHtml(true, "add")}
        <div style="height:12px"></div>
        <button class="btn" type="submit">${t("cal.add_button")}</button>
      </form>
      ${removedHtml}`;
  }

  function render(): void {
    const undoHtml = undo
      ? `<div class="toast" id="undotoast">${t("cal.removed_toast", { label: undo.label })} <button class="linkbtn" type="button" data-act="undo" data-id="${esc(undo.id)}">${t("cal.undo")}</button></div>`
      : "";
    const errHtml = err ? `<p class="error">${esc(err)}</p>` : "";
    const navHtml = periodNav(periodLabel());
    // Read before manageBody() consumes it (below) — a pending quick-add always forces it open,
    // even if the teacher had it closed.
    const openForQuickAdd = quickAddDate !== null;
    if (openForQuickAdd) manageOpen = true;

    app.innerHTML = `
      ${pageHeader({
        eyebrow: term?.name ?? "",
        title: t("cal.title"),
        actions: isLead ? `<a data-link href="/calendar/import" class="btn secondary sm">${t("cal.import")}</a>` : "",
      })}
      ${errHtml}
      ${undoHtml}
      <div id="caltop">
        ${switcher(view)}
        ${navHtml}
        <div id="calview">${viewHtml()}</div>
      </div>
      <details class="cal-manage"${manageOpen ? " open" : ""}>
        <summary>${t("cal.add_title")}</summary>
        <div id="calroot">${manageBody()}</div>
      </details>`;

    // View surface: switch view / navigate period / drill down. #caltop wraps the switcher + nav +
    // view so ONE delegated listener catches them all; it's recreated each render, so listeners
    // never accumulate across navigations.
    const top = app.querySelector<HTMLElement>("#caltop")!;
    top.addEventListener("click", onViewClick);
    const root = app.querySelector<HTMLElement>("#calroot")!;
    root.addEventListener("click", onManageClick);
    root.addEventListener("submit", onManageSubmit);
    wireGradeChips(root);
    // Grade scope hides for `teaching` (never grade-scoped); the "teaching happens" tick re-defaults
    // by type whenever the teacher hasn't touched it themselves yet (ruling 7's per-type default).
    root.addEventListener("change", (e) => {
      const el = e.target as HTMLElement;
      const form = el.closest("form");
      if (!form) return;
      if (el instanceof HTMLSelectElement && el.name === "type") {
        form.querySelector<HTMLElement>(".grade-field")!.hidden = el.value === "teaching";
        const tick = form.querySelector<HTMLInputElement>('input[name="teaching_day"]')!;
        if (!tick.dataset.touched) tick.checked = el.value !== "holiday" && el.value !== "training";
      } else if (el instanceof HTMLInputElement && el.name === "teaching_day") {
        el.dataset.touched = "1"; // a manual tick wins over the type-change default from now on
      }
    });
    // Esc cancels the quick-add (Job B §4) — scoped to the add form so editing an existing row's
    // own Cancel button (already there) stays the way to back out of THAT.
    root.addEventListener("keydown", onAddKeydown);
    // Track manual open/close so the NEXT re-render (any mutate()) preserves it — see manageOpen.
    // Closing (Esc or the teacher collapsing it by hand) also abandons a pending quick-add — the
    // OTHER clear point is a successful add-form submit, in onManageSubmit below.
    app.querySelector<HTMLDetailsElement>("details.cal-manage")!.addEventListener("toggle", (e) => {
      const nowOpen = (e.target as HTMLDetailsElement).open;
      manageOpen = nowOpen;
      if (!nowOpen) {
        quickAddDate = null;
        // A native <details> toggle never runs render(), so the STATE clears but the #cal-date
        // input — the same DOM node — would otherwise sit there still showing the old date if the
        // teacher reopens this same panel by hand (no click-a-day, no render, nothing to refresh
        // it). Clear the field directly so what's visible matches what's remembered.
        const dateInput = app.querySelector<HTMLInputElement>("#cal-date");
        if (dateInput) dateInput.value = "";
      }
    });

    if (openForQuickAdd) {
      const jump = (): void => {
        app.querySelector<HTMLElement>("#addentry")?.scrollIntoView({ block: "center" });
        app.querySelector<HTMLInputElement>("#cal-label")?.focus();
      };
      // The first render happens before the page is on screen (the router swaps it in when ready).
      if (app.isConnected) jump();
      else app.addEventListener("eden:mounted", jump, { once: true });
    }
  }

  function onAddKeydown(e: KeyboardEvent): void {
    if (e.key !== "Escape") return;
    if (!(e.target as HTMLElement).closest("#addentry")) return;
    (e.target as HTMLElement).blur();
    app.querySelector<HTMLDetailsElement>("details.cal-manage")!.open = false;
  }

  // ── view interactions (no server mutation — just navigate the calendar) ──────────────────
  async function onViewClick(e: Event): Promise<void> {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-view],[data-nav],[data-day],[data-week],[data-month]");
    if (!el) return;

    if (el.dataset.view) {
      view = el.dataset.view as ViewKind;
    } else if (el.dataset.nav) {
      const nav = el.dataset.nav;
      if (nav === "today") anchor = todayIso();
      else if (view === "week") {
        // One school-calendar week at a time (a day to a fortnight long), never a fixed 7 days.
        anchor = term
          ? adjacentWeekAnchor(term, weekNoForAnchor(), nav === "prev" ? -1 : 1, nextStart)
          : shiftDays(anchor, nav === "prev" ? -7 : 7);
      }
      else if (view === "month") anchor = shiftMonths(anchor, nav === "prev" ? -1 : 1);
      else if (view === "year") anchor = shiftMonths(anchor, nav === "prev" ? -12 : 12);
      else if (view === "term") {
        // Jump to the adjacent term's start (no-op at the ends of the year).
        const target = nav === "prev" ? prevStart : nextStart;
        if (target) anchor = target;
      }
    } else if (el.dataset.day) {
      // An empty day in Month view opens the add form pre-filled (Job B §4) instead of drilling
      // into Week — there is nothing to browse yet, so go straight to typing it in.
      if (view === "month" && dayIsEmpty(el.dataset.day)) {
        quickAddDate = el.dataset.day;
        render();
        return;
      }
      anchor = el.dataset.day;
      view = "week";
    } else if (el.dataset.week) {
      anchor = weekStartIso(clampWeek(Number(el.dataset.week)));
      view = "week";
    } else if (el.dataset.month) {
      anchor = el.dataset.month;
      view = "month";
    } else return;

    await refresh();
    render();
  }

  // ── manage interactions (the preserved add/edit/remove/undo/restore spine) ───────────────
  async function mutate(run: () => Promise<void>): Promise<void> {
    err = null;
    try {
      await run();
      await refresh();
    } catch (e2) {
      err = (e2 as Error)?.message ?? "Something went wrong.";
    }
    render();
  }

  function onManageClick(e: Event): void {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!btn) return;
    const act = btn.dataset.act!;
    const id = btn.dataset.id ?? null;

    if (act === "edit") { editingId = id; undo = null; render(); return; }
    if (act === "cancel") { editingId = null; render(); return; }
    if (act === "remove" && id) {
      const label = manage.term.find((x) => x.id === id)?.label ?? "Entry";
      void mutate(async () => {
        await api(`/api/calendar/${id}`, { method: "DELETE" });
        editingId = null;
        undo = { id, label };
      });
      return;
    }
    if ((act === "undo" || act === "restore") && id) {
      void mutate(async () => {
        await api(`/api/calendar/${id}/restore`, { method: "POST" });
        undo = null;
      });
      return;
    }
  }

  function onManageSubmit(e: Event): void {
    const form = e.target as HTMLFormElement;
    e.preventDefault();

    if (form.id === "addentry") {
      const fd = new FormData(form);
      const entry_date = String(fd.get("entry_date") ?? "");
      const end_date = String(fd.get("end_date") ?? "").trim() || null;
      const label = String(fd.get("label") ?? "").trim();
      const type = String(fd.get("type") ?? "event");
      if (!entry_date || !label) return;
      const grades = type === "teaching" ? null : readGrades(form);
      const teaching_day = (form.querySelector<HTMLInputElement>('input[name="teaching_day"]')!.checked ? 1 : 0);
      void mutate(async () => {
        await api("/api/calendar", { method: "POST", body: JSON.stringify({ entry_date, end_date, label, type, grades, teaching_day }) });
        quickAddDate = null; // only on success — a failed save keeps the date so nothing is lost
      });
      return;
    }

    if (form.classList.contains("cal-edit")) {
      const id = form.dataset.id!;
      const fd = new FormData(form);
      const entry_date = String(fd.get("entry_date") ?? "");
      const end_date = String(fd.get("end_date") ?? "").trim() || null;
      const label = String(fd.get("label") ?? "").trim();
      const type = String(fd.get("type") ?? "");
      if (!entry_date || !label) return;
      const grades = type === "teaching" ? null : readGrades(form);
      const teaching_day = form.querySelector<HTMLInputElement>('input[name="teaching_day"]')!.checked ? 1 : 0;
      // is_key is only IN the form at all when isLead (editFormRow) — absent for a teacher, so
      // this never sends the field for them and the server's Lead-only guard is never even hit.
      const body: Record<string, unknown> = { entry_date, end_date, label, type, grades, teaching_day };
      if (isLead) body.is_key = fd.get("is_key") === "on";
      void mutate(async () => {
        await api(`/api/calendar/${id}`, { method: "PATCH", body: JSON.stringify(body) });
        editingId = null;
      });
      return;
    }
  }

  await refresh();
  render();
}
